import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

/**
 * Keeps add-on service lines (blouse stitching, fall & pico, …) consistent
 * with their parent product lines after any cart mutation:
 *
 * - A service line whose parent line was removed is removed too.
 * - A service line whose quantity drifted from its parent is synced back.
 * - A parent line whose service lines are missing is removed, together with
 *   any services it still has. Otherwise the order would carry the stitching
 *   request and measurements without the charge.
 *
 * Pairing comes from hidden line item properties written at add time by
 * assets/roots-addon-service.js and assets/roots-suit-stitching.js: the
 * parent line carries `_addon_ref` and `_addon_count`, and each service line
 * carries `_addon_parent_ref` with the same ref.
 *
 * The cart UI hides remove and quantity controls on service lines
 * (snippets/cart-products.liquid), so the parent-removal path only runs when
 * the cart was changed some other way. It's a client-side guard: real
 * enforcement at checkout needs a server-side validation.
 *
 * Every pass either applies a fix (which re-renders the cart) or finds
 * nothing to do, so reconciliation always converges.
 */

const PARENT_KEY = '_addon_ref';
const COUNT_KEY = '_addon_count';
const CHILD_KEY = '_addon_parent_ref';

let dispatchingOwnEvent = false;
let queue = Promise.resolve();

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  // Our own dispatch below re-enters this listener synchronously; skip it.
  if (dispatchingOwnEvent) return;

  const settled = /** @type {{ promise?: Promise<unknown> }} */ (event).promise ?? Promise.resolve();

  // Serialize passes so rapid cart edits can't interleave reconciles.
  queue = queue.then(() =>
    settled
      .catch(() => {})
      .then(reconcile)
      .catch((error) => console.warn('[roots-addon-cart-guard]', error))
  );
});

// Also check once per page load: catches carts that broke before this guard
// existed or were changed without a cart event (e.g. direct /cart API calls).
queue = queue.then(reconcile).catch((error) => console.warn('[roots-addon-cart-guard]', error));

async function reconcile() {
  const cartResponse = await fetch(`${Theme.routes.cart_url}.js`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!cartResponse.ok) return;

  /** @type {{ items: Array<{ key: string, quantity: number, properties: Record<string, string> | null }> }} */
  const cart = await cartResponse.json();

  /** @type {Map<string, { quantity: number, expected: number, keys: string[] }>} */
  const parents = new Map();
  /** @type {Map<string, string[]>} */
  const serviceKeys = new Map();

  for (const item of cart.items) {
    const parentRef = item.properties?.[PARENT_KEY];
    if (parentRef) {
      const parent = parents.get(parentRef) ?? { quantity: 0, expected: 0, keys: [] };
      parent.quantity += item.quantity;
      // Lines added before `_addon_count` existed still had at least one service.
      parent.expected = Math.max(parent.expected, Number(item.properties?.[COUNT_KEY]) || 1);
      parent.keys.push(item.key);
      parents.set(parentRef, parent);
    }

    const childRef = item.properties?.[CHILD_KEY];
    if (childRef) serviceKeys.set(childRef, [...(serviceKeys.get(childRef) ?? []), item.key]);
  }

  /** @type {Record<string, number>} */
  const updates = {};
  for (const item of cart.items) {
    const ref = item.properties?.[CHILD_KEY];
    if (!ref) continue;

    const parentQuantity = parents.get(ref)?.quantity ?? 0;
    if (item.quantity !== parentQuantity) updates[item.key] = parentQuantity;
  }

  // A parent missing any of its paid services goes, with the services it
  // still has; the customer re-adds the piece from its product page.
  for (const [ref, parent] of parents) {
    const services = serviceKeys.get(ref) ?? [];
    if (services.length >= parent.expected) continue;

    for (const key of [...parent.keys, ...services]) updates[key] = 0;
  }

  if (Object.keys(updates).length === 0) return;

  const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
    .map((item) => (item instanceof HTMLElement ? item.dataset.sectionId : null))
    .filter(Boolean);

  const updateResponse = await fetch(Theme.routes.cart_update_url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({ updates, sections: sectionIds.join(',') }),
  });
  if (!updateResponse.ok) return;

  const updatedCart = await updateResponse.json();

  // Notify Horizon's cart components (drawer contents, icon badge, cart page)
  // exactly like a native cart mutation would.
  const deferredEventPromise = CartLinesUpdateEvent.createPromise();

  dispatchingOwnEvent = true;
  try {
    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'update',
        context: 'cart',
        lines: Object.entries(updates).map(([id, quantity]) => ({ id, quantity })),
        promise: deferredEventPromise.promise,
      })
    );
  } finally {
    dispatchingOwnEvent = false;
  }

  deferredEventPromise.resolve({
    cart: CartLinesUpdateEvent.createCartFromAjaxResponse(updatedCart),
    detail: {
      didError: false,
      items: updatedCart.items,
      sections: updatedCart.sections,
      source: 'roots-addon-cart-guard',
    },
  });
}
